import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
const ScrapCostAnalysisDashboard: React.FC = () => {
  // KPI Data
  const kpis = [{
    title: 'Scrap Cost',
    value: '₹285 Cr',
    change: '-8.5%',
    isPositive: true,
    subtitle: 'FY24 total'
  }, {
    title: 'Scrap Rate',
    value: '2.1%',
    change: '-0.4%',
    isPositive: true,
    subtitle: 'Current rate'
  }, {
    title: 'Top Contributor',
    value: 'Dimensional',
    change: '32.5%',
    isPositive: false,
    subtitle: 'Of total scrap'
  }];
  // Scrap Cost Trend Data (Last 12 Months)
  const scrapCostTrendData = [{
    month: 'Jan',
    cost: 28,
    rate: 2.5,
    target: 2.0
  }, {
    month: 'Feb',
    cost: 26,
    rate: 2.4,
    target: 2.0
  }, {
    month: 'Mar',
    cost: 29,
    rate: 2.3,
    target: 2.0
  }, {
    month: 'Apr',
    cost: 25,
    rate: 2.2,
    target: 2.0
  }, {
    month: 'May',
    cost: 27,
    rate: 2.1,
    target: 2.0
  }, {
    month: 'Jun',
    cost: 24,
    rate: 2.1,
    target: 2.0
  }, {
    month: 'Jul',
    cost: 26,
    rate: 2.0,
    target: 2.0
  }, {
    month: 'Aug',
    cost: 23,
    rate: 1.9,
    target: 2.0
  }, {
    month: 'Sep',
    cost: 25,
    rate: 2.0,
    target: 2.0
  }, {
    month: 'Oct',
    cost: 22,
    rate: 1.8,
    target: 2.0
  }, {
    month: 'Nov',
    cost: 24,
    rate: 1.7,
    target: 2.0
  }, {
    month: 'Dec',
    cost: 21,
    rate: 1.6,
    target: 2.0
  }];
  // Scrap by Product Line Data
  const scrapByProductLineData = [{
    productLine: 'Lighting',
    scrapCost: 78,
    scrapRate: 2.3,
    volume: 685000,
    color: '#3b82f6'
  }, {
    productLine: 'Electrical',
    scrapCost: 65,
    scrapRate: 1.8,
    volume: 725000,
    color: '#10b981'
  }, {
    productLine: 'Polymer',
    scrapCost: 72,
    scrapRate: 2.4,
    volume: 588000,
    color: '#f59e0b'
  }, {
    productLine: 'Safety',
    scrapCost: 70,
    scrapRate: 1.9,
    volume: 442000,
    color: '#ef4444'
  }];
  // Scrap Pareto Data
  const scrapParetoData = [{
    category: 'Dimensional Defects',
    cost: 92.6,
    percentage: 32.5,
    cumulativePercent: 32.5
  }, {
    category: 'Material Waste',
    cost: 68.4,
    percentage: 24.0,
    cumulativePercent: 56.5
  }, {
    category: 'Surface Finish',
    cost: 48.5,
    percentage: 17.0,
    cumulativePercent: 73.5
  }, {
    category: 'Assembly Errors',
    cost: 34.2,
    percentage: 12.0,
    cumulativePercent: 85.5
  }, {
    category: 'Tool Wear',
    cost: 22.8,
    percentage: 8.0,
    cumulativePercent: 93.5
  }, {
    category: 'Process Variation',
    cost: 11.4,
    percentage: 4.0,
    cumulativePercent: 97.5
  }, {
    category: 'Others',
    cost: 7.1,
    percentage: 2.5,
    cumulativePercent: 100.0
  }];
  // Scrap Events Log Data
  const scrapEventsData = [{
    date: '2024-02-05',
    time: '14:35',
    line: 'Line 3',
    productLine: 'Polymer',
    partNumber: 'PL-8845',
    quantity: 250,
    scrapCost: 35000,
    scrapRate: 5.2,
    category: 'Dimensional Defects',
    rootCause: 'Tool wear exceeded tolerance',
    operator: 'Operator-15',
    shift: 'B',
    action: 'Tool replacement initiated',
    status: 'Resolved',
    responsibleTeam: 'Maintenance',
    resolutionTime: '2.5 hrs'
  }, {
    date: '2024-02-05',
    time: '11:20',
    line: 'Line 1',
    productLine: 'Lighting',
    partNumber: 'LT-4523',
    quantity: 180,
    scrapCost: 25200,
    scrapRate: 3.8,
    category: 'Material Waste',
    rootCause: 'Raw material quality issue',
    operator: 'Operator-08',
    shift: 'A',
    action: 'Vendor quality audit scheduled',
    status: 'In Progress',
    responsibleTeam: 'Quality',
    resolutionTime: 'Ongoing'
  }, {
    date: '2024-02-04',
    time: '16:45',
    line: 'Line 2',
    productLine: 'Electrical',
    partNumber: 'EL-6732',
    quantity: 320,
    scrapCost: 44800,
    scrapRate: 6.5,
    category: 'Surface Finish',
    rootCause: 'Cooling time insufficient',
    operator: 'Operator-12',
    shift: 'B',
    action: 'Process parameters adjusted',
    status: 'Resolved',
    responsibleTeam: 'Process Engineering',
    resolutionTime: '1.8 hrs'
  }, {
    date: '2024-02-04',
    time: '09:15',
    line: 'Line 4',
    productLine: 'Safety',
    partNumber: 'SF-9921',
    quantity: 145,
    scrapCost: 20300,
    scrapRate: 3.2,
    category: 'Assembly Errors',
    rootCause: 'Operator training gap',
    operator: 'Operator-21',
    shift: 'A',
    action: 'Additional training provided',
    status: 'Resolved',
    responsibleTeam: 'HR & Training',
    resolutionTime: '4.0 hrs'
  }, {
    date: '2024-02-03',
    time: '13:50',
    line: 'Line 5',
    productLine: 'Lighting',
    partNumber: 'LT-3341',
    quantity: 280,
    scrapCost: 39200,
    scrapRate: 5.8,
    category: 'Dimensional Defects',
    rootCause: 'Calibration drift detected',
    operator: 'Operator-18',
    shift: 'B',
    action: 'Equipment recalibrated',
    status: 'Resolved',
    responsibleTeam: 'Maintenance',
    resolutionTime: '3.2 hrs'
  }, {
    date: '2024-02-03',
    time: '10:30',
    line: 'Line 1',
    productLine: 'Polymer',
    partNumber: 'PL-7754',
    quantity: 195,
    scrapCost: 27300,
    scrapRate: 4.1,
    category: 'Material Waste',
    rootCause: 'Material handling damage',
    operator: 'Operator-09',
    shift: 'A',
    action: 'Improved handling procedures',
    status: 'Resolved',
    responsibleTeam: 'Production',
    resolutionTime: '2.0 hrs'
  }, {
    date: '2024-02-02',
    time: '15:25',
    line: 'Line 3',
    productLine: 'Electrical',
    partNumber: 'EL-5512',
    quantity: 210,
    scrapCost: 29400,
    scrapRate: 4.5,
    category: 'Tool Wear',
    rootCause: 'Premature tool failure',
    operator: 'Operator-16',
    shift: 'B',
    action: 'Tool supplier evaluation',
    status: 'In Progress',
    responsibleTeam: 'Procurement',
    resolutionTime: 'Ongoing'
  }, {
    date: '2024-02-02',
    time: '08:40',
    line: 'Line 2',
    productLine: 'Safety',
    partNumber: 'SF-8863',
    quantity: 165,
    scrapCost: 23100,
    scrapRate: 3.6,
    category: 'Process Variation',
    rootCause: 'Temperature fluctuation',
    operator: 'Operator-11',
    shift: 'A',
    action: 'HVAC system optimized',
    status: 'Resolved',
    responsibleTeam: 'Facilities',
    resolutionTime: '5.5 hrs'
  }, {
    date: '2024-02-01',
    time: '17:10',
    line: 'Line 4',
    productLine: 'Polymer',
    partNumber: 'PL-6621',
    quantity: 235,
    scrapCost: 32900,
    scrapRate: 4.9,
    category: 'Surface Finish',
    rootCause: 'Contamination in process',
    operator: 'Operator-22',
    shift: 'B',
    action: 'Enhanced cleaning protocol',
    status: 'Resolved',
    responsibleTeam: 'Quality',
    resolutionTime: '2.8 hrs'
  }, {
    date: '2024-02-01',
    time: '12:55',
    line: 'Line 5',
    productLine: 'Lighting',
    partNumber: 'LT-2234',
    quantity: 175,
    scrapCost: 24500,
    scrapRate: 3.7,
    category: 'Dimensional Defects',
    rootCause: 'Fixture misalignment',
    operator: 'Operator-19',
    shift: 'A',
    action: 'Fixture realigned and verified',
    status: 'Resolved',
    responsibleTeam: 'Maintenance',
    resolutionTime: '1.5 hrs'
  }, {
    date: '2024-01-31',
    time: '14:20',
    line: 'Line 1',
    productLine: 'Electrical',
    partNumber: 'EL-9987',
    quantity: 290,
    scrapCost: 40600,
    scrapRate: 6.0,
    category: 'Assembly Errors',
    rootCause: 'Work instruction unclear',
    operator: 'Operator-07',
    shift: 'B',
    action: 'Work instructions updated',
    status: 'Resolved',
    responsibleTeam: 'Process Engineering',
    resolutionTime: '3.5 hrs'
  }, {
    date: '2024-01-31',
    time: '09:45',
    line: 'Line 3',
    productLine: 'Safety',
    partNumber: 'SF-7745',
    quantity: 155,
    scrapCost: 21700,
    scrapRate: 3.4,
    category: 'Material Waste',
    rootCause: 'Incorrect material grade',
    operator: 'Operator-14',
    shift: 'A',
    action: 'Material verification process enhanced',
    status: 'Resolved',
    responsibleTeam: 'Warehouse',
    resolutionTime: '4.2 hrs'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Resolved':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Dimensional Defects':
        return 'bg-red-100 text-red-700';
      case 'Material Waste':
        return 'bg-orange-100 text-orange-700';
      case 'Surface Finish':
        return 'bg-yellow-100 text-yellow-700';
      case 'Assembly Errors':
        return 'bg-blue-100 text-blue-700';
      case 'Tool Wear':
        return 'bg-purple-100 text-purple-700';
      case 'Process Variation':
        return 'bg-pink-100 text-pink-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const PARETO_COLORS = ['#ef4444', '#f59e0b', '#eab308', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280'];
  return <div data-id="element-6547">
      <DescriptionPanel title="Scrap Cost Analysis" description="Comprehensive scrap cost tracking and analysis across all production lines. Monitor scrap trends, identify top contributors, and analyze detailed scrap events to drive waste reduction and cost savings initiatives." insights={['Scrap cost reduced to ₹285 Cr - 8.5% improvement YoY through targeted initiatives', 'Dimensional defects account for 32.5% of scrap cost - primary focus area', 'Polymer product line has highest scrap rate at 2.4% - process optimization needed', 'Line 3 shows highest scrap events - equipment maintenance and operator training required']} data-id="element-6548" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-6549">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-6550">
          Scrap Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-6551">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-6552">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-6553">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-6554">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-6555">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-6556">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-6557" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-6558" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Scrap Cost Trend */}
      <Card className="p-6 mb-8" data-id="element-6559">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-6560">
          Scrap Cost & Rate Trend (Last 12 Months)
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-6561">
          Monthly scrap cost (₹ Cr) and scrap rate (%)
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-6562">
          <ComposedChart data={scrapCostTrendData} data-id="element-6563">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-6564" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-6565" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} label={{
            value: 'Scrap Cost (₹ Cr)',
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 12
            }
          }} data-id="element-6566" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 3]} tick={{
            fontSize: 12
          }} label={{
            value: 'Scrap Rate (%)',
            angle: 90,
            position: 'insideRight',
            style: {
              fontSize: 12
            }
          }} data-id="element-6567" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-6568" />
            <Legend data-id="element-6569" />
            <Bar yAxisId="left" dataKey="cost" fill="#ef4444" name="Scrap Cost (₹ Cr)" data-id="element-6570" />
            <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#f59e0b" strokeWidth={3} name="Scrap Rate (%)" dot={{
            r: 4
          }} data-id="element-6571" />
            <Line yAxisId="right" type="monotone" dataKey="target" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Target Rate (%)" dot={false} data-id="element-6572" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6573">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-6574">
            <div className="text-center" data-id="element-6575">
              <div className="text-gray-600" data-id="element-6576">Current Month</div>
              <div className="text-xl font-bold text-red-600" data-id="element-6577">₹21 Cr</div>
              <div className="text-xs text-gray-500" data-id="element-6578">1.6% scrap rate</div>
            </div>
            <div className="text-center" data-id="element-6579">
              <div className="text-gray-600" data-id="element-6580">YTD Total</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-6581">₹285 Cr</div>
              <div className="text-xs text-gray-500" data-id="element-6582">FY24 total</div>
            </div>
            <div className="text-center" data-id="element-6583">
              <div className="text-gray-600" data-id="element-6584">Avg Monthly</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-6585">₹23.8 Cr</div>
              <div className="text-xs text-gray-500" data-id="element-6586">12-month average</div>
            </div>
            <div className="text-center" data-id="element-6587">
              <div className="text-gray-600" data-id="element-6588">Improvement</div>
              <div className="text-xl font-bold text-green-600" data-id="element-6589">-36%</div>
              <div className="text-xs text-gray-500" data-id="element-6590">vs Jan rate</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Scrap by Product Line and Scrap Pareto */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-6591">
        {/* Scrap by Product Line */}
        <Card className="p-6" data-id="element-6592">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-6593">
            Scrap by Product Line
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-6594">
            Scrap cost and rate by product line
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-6595">
            <ComposedChart data={scrapByProductLineData} layout="vertical" data-id="element-6596">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-6597" />
              <XAxis type="number" tick={{
              fontSize: 11
            }} data-id="element-6598" />
              <YAxis dataKey="productLine" type="category" width={80} tick={{
              fontSize: 11
            }} data-id="element-6599" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-6600" />
              <Legend data-id="element-6601" />
              <Bar dataKey="scrapCost" fill="#ef4444" name="Scrap Cost (₹ Cr)" data-id="element-6602">
                {scrapByProductLineData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-6603" />)}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6604">
            <div className="space-y-2" data-id="element-6605">
              {scrapByProductLineData.map((item, index) => <div key={index} className="flex items-center justify-between text-sm" data-id="element-6606">
                  <div className="flex items-center" data-id="element-6607">
                    <div className="w-3 h-3 rounded mr-2" style={{
                  backgroundColor: item.color
                }} data-id="element-6608" />
                    <span className="text-gray-700" data-id="element-6609">{item.productLine}</span>
                  </div>
                  <div className="text-right" data-id="element-6610">
                    <span className="font-semibold text-gray-900" data-id="element-6611">
                      ₹{item.scrapCost} Cr
                    </span>
                    <span className="text-xs text-gray-600 ml-2" data-id="element-6612">
                      ({item.scrapRate}% rate)
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </Card>
        {/* Scrap Pareto */}
        <Card className="p-6" data-id="element-6613">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-6614">
            Scrap Pareto Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-6615">
            Focus on top scrap categories driving costs
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-6616">
            <ComposedChart data={scrapParetoData} data-id="element-6617">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-6618" />
              <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} tick={{
              fontSize: 10
            }} data-id="element-6619" />
              <YAxis yAxisId="left" tick={{
              fontSize: 11
            }} label={{
              value: 'Cost (₹ Cr)',
              angle: -90,
              position: 'insideLeft',
              style: {
                fontSize: 11
              }
            }} data-id="element-6620" />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{
              fontSize: 11
            }} label={{
              value: 'Cumulative %',
              angle: 90,
              position: 'insideRight',
              style: {
                fontSize: 11
              }
            }} data-id="element-6621" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-6622" />
              <Legend data-id="element-6623" />
              <Bar yAxisId="left" dataKey="cost" name="Scrap Cost (₹ Cr)" data-id="element-6624">
                {scrapParetoData.map((entry, index) => <Cell key={`cell-${index}`} fill={PARETO_COLORS[index % PARETO_COLORS.length]} data-id="element-6625" />)}
              </Bar>
              <Line yAxisId="right" type="monotone" dataKey="cumulativePercent" stroke="#1f2937" strokeWidth={3} name="Cumulative %" dot={{
              r: 4,
              fill: '#1f2937'
            }} data-id="element-6626" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6627">
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-6628">
              <div className="text-center" data-id="element-6629">
                <div className="text-gray-600" data-id="element-6630">Top Category</div>
                <div className="text-lg font-bold text-red-600" data-id="element-6631">
                  Dimensional
                </div>
                <div className="text-xs text-gray-500" data-id="element-6632">₹92.6 Cr (32.5%)</div>
              </div>
              <div className="text-center" data-id="element-6633">
                <div className="text-gray-600" data-id="element-6634">80/20 Point</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-6635">
                  Top 3 Categories
                </div>
                <div className="text-xs text-gray-500" data-id="element-6636">73.5% of cost</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Scrap Events Log Table */}
      <Card className="p-6" data-id="element-6637">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-6638">
          Scrap Events Log
        </h3>
        <div className="overflow-x-auto" data-id="element-6639">
          <table className="w-full" data-id="element-6640">
            <thead data-id="element-6641">
              <tr className="border-b-2 border-gray-300" data-id="element-6642">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6643">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6644">
                  Time
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6645">
                  Line
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6646">
                  Product Line
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6647">
                  Part Number
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6648">
                  Qty
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6649">
                  Scrap Cost
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6650">
                  Rate %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6651">
                  Category
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6652">
                  Root Cause
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6653">
                  Operator
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6654">
                  Shift
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6655">
                  Corrective Action
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6656">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6657">
                  Responsible Team
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6658">
                  Resolution Time
                </th>
              </tr>
            </thead>
            <tbody data-id="element-6659">
              {scrapEventsData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-6660">
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6661">
                    {row.date}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6662">
                    {row.time}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-6663">
                    {row.line}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6664">
                    {row.productLine}
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-gray-700" data-id="element-6665">
                    {row.partNumber}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-6666">
                    {row.quantity}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-red-600" data-id="element-6667">
                    ₹{(row.scrapCost / 1000).toFixed(1)}K
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-6668">
                    {row.scrapRate.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6669">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getCategoryColor(row.category)}`} data-id="element-6670">
                      {row.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-6671">
                    {row.rootCause}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6672">
                    {row.operator}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6673">
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700" data-id="element-6674">
                      {row.shift}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-6675">
                    {row.action}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6676">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-6677">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6678">
                    {row.responsibleTeam}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6679">
                    {row.resolutionTime}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-6680">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-6681">
            <div className="text-center" data-id="element-6682">
              <div className="text-gray-600" data-id="element-6683">Total Events</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-6684">12</div>
              <div className="text-xs text-gray-500" data-id="element-6685">last 5 days</div>
            </div>
            <div className="text-center" data-id="element-6686">
              <div className="text-gray-600" data-id="element-6687">Total Qty Scrapped</div>
              <div className="text-xl font-bold text-red-600" data-id="element-6688">2,600</div>
              <div className="text-xs text-gray-500" data-id="element-6689">units</div>
            </div>
            <div className="text-center" data-id="element-6690">
              <div className="text-gray-600" data-id="element-6691">Total Cost</div>
              <div className="text-xl font-bold text-red-600" data-id="element-6692">₹364K</div>
              <div className="text-xs text-gray-500" data-id="element-6693">last 5 days</div>
            </div>
            <div className="text-center" data-id="element-6694">
              <div className="text-gray-600" data-id="element-6695">Resolved</div>
              <div className="text-xl font-bold text-green-600" data-id="element-6696">10</div>
              <div className="text-xs text-gray-500" data-id="element-6697">83% resolution</div>
            </div>
            <div className="text-center" data-id="element-6698">
              <div className="text-gray-600" data-id="element-6699">Avg Resolution</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-6700">2.9 hrs</div>
              <div className="text-xs text-gray-500" data-id="element-6701">mean time</div>
            </div>
            <div className="text-center" data-id="element-6702">
              <div className="text-gray-600" data-id="element-6703">Top Line</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-6704">Line 3</div>
              <div className="text-xs text-gray-500" data-id="element-6705">2 events</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default ScrapCostAnalysisDashboard;